import { IconCheck } from "@tabler/icons-react";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";

import classes from "./ValidateButton.module.css";

type VariantStyle = {
  bg: string;
  border: string;
  hoverBg: string;
  hoverBorder: string;
};

const VARIANT_STYLES: Record<"off" | "on" | "offDisabled" | "onDisabled", VariantStyle> = {
  off: {
    bg: colors.bg.elevation.primary,
    border: colors.border.secondaryDefault,
    hoverBg: colors.bg.status.success.primary,
    hoverBorder: colors.icon.success,
  },
  on: {
    bg: colors.bg.status.success.tertiary,
    border: "transparent",
    hoverBg: colors.bg.status.success.tertiary,
    hoverBorder: "transparent",
  },
  offDisabled: {
    bg: colors.bg.elevation.secondary,
    border: colors.border.secondaryDisabled,
    hoverBg: colors.bg.elevation.secondary,
    hoverBorder: colors.border.secondaryDisabled,
  },
  onDisabled: {
    bg: colors.bg.button.primary.disabled,
    border: "transparent",
    hoverBg: colors.bg.button.primary.disabled,
    hoverBorder: "transparent",
  },
};

const getVariantStyle = (checked: boolean, disabled: boolean): VariantStyle => {
  const key = disabled ? (checked ? "onDisabled" : "offDisabled") : checked ? "on" : "off";
  return VARIANT_STYLES[key];
};

type ValidateButtonProps = {
  checked?: boolean;
  disabled?: boolean;
  onChange?: () => void;
  "aria-label"?: string;
};

const ValidateButton = ({
  checked = false,
  disabled = false,
  onChange,
  "aria-label": ariaLabel,
}: ValidateButtonProps) => {
  const { bg, border, hoverBg, hoverBorder } = getVariantStyle(checked, disabled);

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      className={classes.root}
      onClick={!disabled ? onChange : undefined}
      style={
        {
          "--vb-bg": bg,
          "--vb-border": border,
          "--vb-hover-bg": hoverBg,
          "--vb-hover-border": hoverBorder,
          "--vb-root-size": spacing[6],
          "--vb-root-padding": spacing[0.5],
        } as React.CSSProperties
      }
    >
      <span className={classes.inner}>
        {checked && <IconCheck size={12} stroke={2.5} color={colors.text.primaryInverse} />}
      </span>
    </button>
  );
};

export { ValidateButton };
